<template>
  <div>
    <div class="row mb-4">
      <div class="col-12">
        <Card cardClasses="border-0 shadow-sm rounded-4">
          <template v-slot:header>
            <div class="d-flex align-items-center justify-content-between p-3">
              <div class="d-flex align-items-center">
                <div class="icon-circle bg-primary text-white me-3">
                  <vue-feather type="file-text" size="20"></vue-feather>
                </div>
                <div>
                  <h4 class="mb-0 fw-bold text-primary">Payslip Templates</h4>
                  <p class="text-muted mb-0 small">Customize your payslip appearance ({{templates.length}} available)</p>
                </div>
              </div>
            </div>
          </template>

          <!-- Template Colors Section -->
          <div class="p-3">
            <div class="card border-0 shadow-sm rounded-4 mb-4">
              <div class="card-header bg-light d-flex align-items-center">
                <div class="icon-circle-sm bg-primary text-white me-2">
                  <vue-feather type="droplet" size="14"></vue-feather>
                </div>
                <h5 class="mb-0 fw-bold">Template Colors</h5>
              </div>
              <div class="card-body">
                <form @submit.prevent="">
                  <div class="row g-3">
                    <div class="form-group col-md-5">
                      <label for="lightColor" class="form-label fw-medium">Light color:</label>
                      <div class="input-group">
                        <span class="input-group-text bg-light">
                          <vue-feather type="sun" size="16"></vue-feather>
                        </span>
                        <input type="color" class="form-control form-control-color shadow-sm w-100" id="lightColor" v-model="organization.light_color">
                      </div>
                      <small class="text-muted">Select a light color for your template</small>
                    </div>
                    <div class="form-group col-md-5">
                      <label for="darkColor" class="form-label fw-medium">Dark color:</label>
                      <div class="input-group">
                        <span class="input-group-text bg-light">
                          <vue-feather type="moon" size="16"></vue-feather>
                        </span>
                        <input type="color" class="form-control form-control-color shadow-sm w-100" id="darkColor" v-model="organization.dark_color">
                      </div>
                      <small class="text-muted">Select a dark color for your template</small>
                    </div>
                    <div class="form-group col-md-2 d-flex align-items-end">
                      <button type="button" class="btn btn-primary rounded-pill w-100" @click="updateColors">
                        <vue-feather type="save" size="16" class="me-1"></vue-feather>
                        Save Colors
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-5">
            <Spinner spinner-class="mb-1" />
            <p class="text-muted">Loading template data...</p>
          </div>

          <!-- Templates Grid -->
          <div v-else class="px-3 pb-4">
            <div class="card border-0 shadow-sm rounded-4">
              <div class="card-header bg-light d-flex align-items-center">
                <div class="icon-circle-sm bg-primary text-white me-2">
                  <vue-feather type="grid" size="14"></vue-feather>
                </div>
                <h5 class="mb-0 fw-bold">Available Templates</h5>
              </div>
              <div class="card-body">
                <div class="row g-4">
                  <div v-for="template in templates" :key="template.id" class="col-xl-3 col-lg-4 col-md-6">
                    <div class="template-card card h-100 border-0 shadow-sm rounded-4">
                      <div class="card-header bg-light text-center py-3">
                        <h5 class="mb-0 fw-bold">{{template.description}}</h5>
                      </div>
                      <div class="card-body text-center p-3">
                        <img :src="apiURL + template.image" alt="template" class="img-fluid rounded-3 mb-3 shadow-sm">
                        <div class="d-grid gap-2">
                          <button class="btn btn-info rounded-pill" @click="selectTemplate(template.id); previewModal = true">
                            <vue-feather type="eye" size="16" class="me-1"></vue-feather>
                            Preview
                          </button>
                          <button
                              class="btn rounded-pill"
                              :class="template.id === organization.template_id ? 'btn-success' : 'btn-primary'"
                              :disabled="template.id === organization.template_id"
                              @click="changeTemplate(template.id)">
                            <vue-feather :type="template.id === organization.template_id ? 'check-circle' : 'check'" size="16" class="me-1"></vue-feather>
                            {{ getName(template.id) }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  </div>
</template>

<script>

import accountService from "@/api/accountService";
import {decrypt} from "@/utils/crypto";
import VueFeather from 'vue-feather';

export default {
  name: "Templates",
  components: {
    VueFeather
  },

  data() {
    return {
      templates: [],
      templateHtml: '',
      organization: {},
      previewModal: false,
      loading: true,
      apiURL: process.env.VUE_APP_API_PUBLIC_URL,
      baseURL: process.env.VUE_APP_BASE_URL
    }
  },

  methods: {
    async updateColors() {
      await accountService.updateColors({
        light_color: this.organization.light_color,
        dark_color: this.organization.dark_color
      })
      await this.$router.go(0)
    },
    checkTemplate(id) {
      if (this.organization.template_id === id) {
        return 'disabled btn-outline-dark'
      }
    },
    async changeTemplate(id) {
      await accountService.setTemplate(id)
      await this.$router.go(0)
    },
    getName(id) {
      return this.organization.template_id === id ? 'Current' : 'Apply'
    },
    selectTemplate(id) {
      window.open(this.baseURL + 'preview/' + id, '_blank')
    }
  },

  async created() {
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    this.templates = await accountService.getTemplates()
    this.loading = false
  },
}
</script>

<style scoped>
/* Icon styles */
.icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-circle-sm {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Card styles */
.template-card {
  transition: all 0.3s ease;
}

.template-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
}

/* Button hover effects */
.btn-primary.rounded-pill:hover,
.btn-info.rounded-pill:hover,
.btn-success.rounded-pill:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .form-group {
    margin-bottom: 1rem;
  }
}
</style>
